import React, { useRef, useState } from 'react';
import styles from './mini-booking.module.scss';
import { Alert, TransparentButton } from 'shared/ui';
import {
  StepStart,
  StepService,
  StepEmployee,
  StepDateConfirm,
  StepTime,
  StepCustomerInfo,
  StepCustomerInfoRef,
  StepSummary,
} from './steps';
import { BookingApi } from 'shared/api';
import { businessStore } from 'entities/index';
import * as dateFns from 'date-fns';
import { ru } from 'date-fns/locale';
import { monthCalendarStore } from 'entities/calendar/model/month-calendar.store';
import { dayCalendarStore } from 'entities/calendar/model/day-calendar.store';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { BookingTypeEnum } from 'shared/api/booking/interfaces/booking-type.enum';
import { ICalendarDate } from 'entities/calendar/model/calendar-date';

interface IBookingSuccess {
  phone: string;
}

interface IMiniBooking {
  date: ICalendarDate;
}

type Step =
  | 'start'
  | 'service'
  | 'employee'
  | 'date'
  | 'time'
  | 'customer'
  | 'summary';

export const MiniBooking: React.FC<IMiniBooking> = observer(({ date }) => {
  const [step, setStep] = useState<Step>('start');
  const [selectedTime, setSelectedTime] = useState('');
  const [customerData, setCustomerData] = useState({
    name: '',
    phone: '',
    comment: '',
  });
  const [history, setHistory] = useState<Step[]>([]); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [bookingSuccess, setBookingSuccess] = useState<IBookingSuccess | null>(
    null
  );
  const params = useParams();
  const pointId = businessStore.pointId || params.pointId;
  const customerInfoRef = useRef<StepCustomerInfoRef>(null);

  const dateFormatted = new Date(date.year, date.month, date.day);

  const goNext = (nextStep: Step) => {
    setHistory((prev) => [...prev, step]);
    setStep(nextStep);
  };

  const goBack = () => {
    const currentStep = step;
    setHistory((prev) => {
      const newHistory = [...prev];
      const prevStep = newHistory.pop();
      if (prevStep) {
        setStep(prevStep);
      }
      return newHistory;
    });

    switch (currentStep) {
      case 'service':
        monthCalendarStore.serviceId = '';
        break;
      case 'employee':
        monthCalendarStore.employeeId = '';
        break;
      case 'time':
        setSelectedTime('');
        break;
      case 'customer':
        setCustomerData({ name: '', phone: '', comment: '' });
        break;
    }
  };

  const clearData = () => {
    monthCalendarStore.serviceId = '';
    monthCalendarStore.employeeId = '';
    setSelectedTime('');
    setCustomerData({ name: '', phone: '', comment: '' });
    setStep('start');
    setHistory([]);
    if (pointId) {
      monthCalendarStore.fetchCalendarMonthInfo({
        month: date.month,
        year: date.year,
        pointId: pointId,
      });
    }
  };

  const setServiceHandler = (id: string) => {
    monthCalendarStore.serviceId = id || undefined;
  };

  const setEmployeeHandler = (id: string) => {
    monthCalendarStore.employeeId = id || undefined;
  };

  const createBooking = () => {
    if (pointId && monthCalendarStore.serviceId && selectedTime) {
      const params: any = {
        pointId,
        customerPhone: customerData.phone || undefined,
        customerName: customerData.name || undefined,
        comment: customerData.comment || undefined,
        serviceId: monthCalendarStore.serviceId,
        startAt: (() => {
          const [hours, minutes] = selectedTime.split(':');
          const dateWithTime = new Date(dateFormatted);
          dateWithTime.setHours(
            parseInt(hours, 10),
            parseInt(minutes, 10),
            0,
            0
          );
          return dateFns.format(dateWithTime, "yyyy-MM-dd'T'HH:mm:ssxxx");
        })(),
        type: BookingTypeEnum.CLIENT_SELF,
      };
      if (monthCalendarStore.employeeId) {
        params.employeeId = monthCalendarStore.employeeId;
      }
      BookingApi.createBooking(params)
        .then(() => {
          setAlertMessage('Запись создана');
          setBookingSuccess({ phone: customerData.phone });
          clearData();
        })
        .catch((error) =>
          setAlertMessage(
            error instanceof Error
              ? error.message
              : 'Ошибка при создании записи'
          )
        );
    }
  };

  const getServiceData = (): {
    name: string | undefined;
    duration: string | undefined;
    endTime: string | undefined;
    price: string | undefined;
  } => {
    const service = businessStore.servicesList?.find(
      (s) => s.id === monthCalendarStore.serviceId
    );
    const duration = String(service?.duration);
    let endTime: string | undefined;
    if (selectedTime && service?.duration) {
      const [hours, minutes] = selectedTime.split(':').map(Number);
      const totalMinutes = hours * 60 + minutes + service.duration;
      const endHours = Math.floor(totalMinutes / 60) % 24;
      const endMinutes = totalMinutes % 60;
      endTime = `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
    }
    return {
      name: service?.name,
      duration,
      endTime,
      price: String(service?.price ?? ''),
    };
  };

  const getEmployeeName = (): string | undefined => {
    if (!monthCalendarStore.employeeId) return 'Любой специалист';
    const allEmployees = businessStore.servicesList
      ?.flatMap((s) => s.employees)
      .filter(
        (emp, idx, self) => idx === self.findIndex((e) => e.id === emp.id)
      );
    const employee = allEmployees?.find(
      (e) => e.id === monthCalendarStore.employeeId
    );
    return employee?.fio;
  };

  const getFormattedDate = (): string => {
    const selectedDate = dayCalendarStore.getSelectedDateAsDate;
    return dateFns.format(selectedDate, 'dd MMMM yyyy', { locale: ru });
  };

  const renderSuccessView = () => {
    return (
      <div className={styles.success__container}>
        <div className={styles.success__icon}>📅</div>
        <div className={styles.success__title}>Вы записаны!</div>
        <div className={styles.success__info}>
          {getServiceData().name} — {getEmployeeName()}
          <br />
          {getFormattedDate()}, {selectedTime}
        </div>
      </div>
    );
  };

  const renderStep = () => {
    if (bookingSuccess) {
      return renderSuccessView();
    }
    switch (step) {
      case 'start':
        return (
          <StepStart
            onSelectService={() => goNext('service')}
            onSelectEmployee={() => goNext('employee')}
          />
        );
      case 'service':
        return (
          <StepService
            selectedServiceId={monthCalendarStore.serviceId}
            employeeId={monthCalendarStore.employeeId}
            onSelect={(id) => {
              setServiceHandler(id);
              goNext(history.includes('employee') ? 'date' : 'employee');
            }}
          />
        );
      case 'employee':
        return (
          <StepEmployee
            selectedEmployeeId={monthCalendarStore.employeeId}
            serviceId={monthCalendarStore.serviceId}
            onSelect={(id) => {
              setEmployeeHandler(id);
              goNext(monthCalendarStore.serviceId ? 'date' : 'service');
            }}
          />
        );
      case 'date':
        return <StepDateConfirm />;
      case 'time':
        return (
          <StepTime
            employeeId={monthCalendarStore.employeeId}
            serviceId={monthCalendarStore.serviceId}
            date={dateFormatted}
            selectedTime={selectedTime}
            onSelect={(time) => {
              setSelectedTime(time);
              goNext('customer');
            }}
          />
        );
      case 'customer':
        return (
          <StepCustomerInfo
            ref={customerInfoRef}
            name={customerData.name}
            phone={customerData.phone}
            comment={customerData.comment}
            onNameChange={(name) => setCustomerData({ ...customerData, name })}
            onPhoneChange={(phone) =>
              setCustomerData({ ...customerData, phone })
            }
            onCommentChange={(comment) =>
              setCustomerData({ ...customerData, comment })
            }
          />
        );
      case 'summary':
        return (
          <StepSummary
            data={{
              service: getServiceData().name,
              serviceDuration: getServiceData().duration,
              serviceEndTime: getServiceData().endTime,
              price: getServiceData().price,
              employee: getEmployeeName(),
              date: getFormattedDate(),
              time: selectedTime,
              name: customerData.name,
              phone: customerData.phone,
              comment: customerData.comment,
            }}
          />
        );
      default:
        return null;
    }
  };

  const renderButtons = () => {
    if (bookingSuccess) {
      return (
        <div className={styles.content__buttons}>
          <TransparentButton
            text="Закрыть"
            onClick={() => setBookingSuccess(null)}
            className={styles.button_back}
          />
        </div>
      );
    }
    switch (step) {
      case 'start':
        return null;
      case 'service':
      case 'employee':
      case 'date':
      case 'time':
      case 'customer':
        return (
          <div className={styles.content__buttons}>
            <TransparentButton
              text="Назад"
              onClick={goBack}
              className={styles.button_back}
            />
            <TransparentButton
              text="Далее"
              onClick={() => {
                if (step === 'customer') {
                  if (customerInfoRef.current?.validate()) {
                    goNext('summary');
                  }
                } else if (step === 'service') {
                  if (monthCalendarStore.serviceId) {
                    goNext(history.includes('employee') ? 'date' : 'employee');
                  }
                } else if (step === 'employee') {
                  if (monthCalendarStore.serviceId) {
                    goNext('date');
                  } else {
                    goNext('service');
                  }
                } else if (step === 'date') {
                  goNext('time');
                } else if (step === 'time') {
                  if (selectedTime) {
                    goNext('customer');
                  }
                }
              }}
              color="green"
              className={styles.button_next}
            />
          </div>
        );
      case 'summary':
        return (
          <div className={styles.content__buttons}>
            <TransparentButton
              text="Назад"
              onClick={goBack}
              className={styles.button_back}
            />
            <TransparentButton
              text="Записаться"
              onClick={createBooking}
              color="green"
              className={styles.button_submit}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className={styles.booking__container}>
        <div className={styles.container__title}>
          Запись в {businessStore.pointHeaderData?.name || ''}
        </div>
        <div className={styles.container__content}>
          <div className={styles.content__step}>{renderStep()}</div>
          {renderButtons()}
        </div>
      </div>
      {alertMessage && (
        <Alert
          type={alertMessage.includes('создана') ? 'success' : 'error'}
          message={alertMessage}
          onClose={() => setAlertMessage(null)}
        />
      )}
    </>
  );
});
